// src/components/CustomerPortalToggle.js
import React, { useState } from "react";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Card,
  CardContent,
  TextField,
  Stack,
  Chip,
  Alert,
  Collapse,
  Button,
  InputAdornment,
  IconButton,
  Tooltip,
} from "@mui/material";
import {
  Visibility,
  VisibilityOff,
  VpnKey,
  InfoOutlined,
} from "@mui/icons-material";

// Generate secure random password
const generatePassword = () => {
  const length = 12;
  const charset = "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789!@#$%^&*";
  let password = "";
  for (let i = 0; i < length; i++) {
    password += charset.charAt(Math.floor(Math.random() * charset.length));
  }
  return password;
};

/**
 * CustomerPortalToggle Component
 * 
 * Use this component in customer creation forms to automatically
 * enable portal access with generated credentials.
 * 
 * Usage:
 * <CustomerPortalToggle
 *   customerEmail={customerEmail}
 *   onPortalDataChange={(portalData) => setPortalData(portalData)}
 *   defaultEnabled={true}
 * />
 */
function CustomerPortalToggle({ 
  customerEmail = "", 
  onPortalDataChange,
  defaultEnabled = true,
  compact = false,
}) {
  const [portalEnabled, setPortalEnabled] = useState(defaultEnabled);
  const [portalEmail, setPortalEmail] = useState(customerEmail);
  const [portalPassword, setPortalPassword] = useState(generatePassword());
  const [showPassword, setShowPassword] = useState(false);

  // Update parent component when portal data changes
  React.useEffect(() => {
    if (onPortalDataChange) {
      onPortalDataChange({
        portalEnabled,
        portalEmail: portalEmail || customerEmail,
        portalPassword,
      });
    }
  }, [portalEnabled, portalEmail, portalPassword, customerEmail, onPortalDataChange]);

  // Auto-update portal email when customer email changes
  React.useEffect(() => {
    if (customerEmail && !portalEmail) {
      setPortalEmail(customerEmail);
    }
  }, [customerEmail, portalEmail]);

  const handleToggle = (checked) => {
    setPortalEnabled(checked);
    // Generate new password when enabling
    if (checked && !portalPassword) {
      setPortalPassword(generatePassword());
    }
  };

  const handleGeneratePassword = () => {
    const newPassword = generatePassword();
    setPortalPassword(newPassword);
  };

  // Compact version for inline forms
  if (compact) {
    return (
      <Box sx={{ mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={portalEnabled}
              onChange={(e) => handleToggle(e.target.checked)}
            />
          }
          label={
            <Box>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                Enable Customer Portal Access
              </Typography>
              {portalEnabled && (
                <Typography variant="caption" color="success.main">
                  ✓ Auto-generated credentials
                </Typography>
              )}
            </Box>
          }
        />
      </Box>
    );
  }

  // Full version with credential display
  return (
    <Card sx={{ bgcolor: portalEnabled ? "rgba(245,158,11,0.05)" : "background.paper" }}>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 2 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Typography variant="h6" sx={{ fontWeight: 600 }}>
              🌐 Customer Portal
            </Typography>
            <Tooltip title="Allow customer to login and self-service online">
              <InfoOutlined fontSize="small" color="action" />
            </Tooltip>
          </Box>
          <Chip
            label={portalEnabled ? "Will be enabled" : "Disabled"}
            color={portalEnabled ? "success" : "default"}
            size="small"
          />
        </Box>

        {/* Enable/Disable Toggle */}
        <FormControlLabel
          control={
            <Switch
              checked={portalEnabled}
              onChange={(e) => handleToggle(e.target.checked)}
            />
          }
          label={
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Enable portal access for this customer
            </Typography>
          }
          sx={{ mb: 2 }}
        />

        {/* Credentials Section (shown when enabled) */}
        <Collapse in={portalEnabled}>
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Portal credentials will be automatically generated and saved with the customer.
            </Alert>

            {/* Portal Email */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Portal Email
              </Typography>
              <TextField
                fullWidth
                value={portalEmail || customerEmail}
                onChange={(e) => setPortalEmail(e.target.value)}
                placeholder="customer@email.com"
                size="small"
                helperText="Customer will use this email to login"
              />
            </Box>

            {/* Portal Password */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
                Portal Password
              </Typography>
              <TextField
                fullWidth
                type={showPassword ? "text" : "password"}
                value={portalPassword}
                onChange={(e) => setPortalPassword(e.target.value)}
                size="small"
                helperText="Secure password auto-generated"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <Tooltip title={showPassword ? "Hide" : "Show"}>
                        <IconButton
                          size="small"
                          onClick={() => setShowPassword(!showPassword)}
                          edge="end"
                        >
                          {showPassword ? <VisibilityOff /> : <Visibility />}
                        </IconButton>
                      </Tooltip>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>

            {/* Generate New Password Button */}
            <Button
              variant="outlined"
              size="small"
              startIcon={<VpnKey />}
              onClick={handleGeneratePassword}
              fullWidth
            >
              Generate New Password
            </Button>

            {/* Info Box */}
            <Box sx={{ mt: 2, p: 2, bgcolor: "rgba(59,130,246,0.05)", borderRadius: 2 }}>
              <Typography variant="caption" color="text.secondary">
                📋 After creating the customer:
              </Typography>
              <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
                <Typography variant="caption" color="text.secondary" component="li">
                  Customer can login at: {window.location.origin}
                </Typography>
                <Typography variant="caption" color="text.secondary" component="li">
                  You can send them an invite email from their detail page
                </Typography>
                <Typography variant="caption" color="text.secondary" component="li">
                  They'll be able to request jobs and view invoices
                </Typography>
              </Box>
            </Box>
          </Box>
        </Collapse>
      </CardContent>
    </Card>
  );
}

export default CustomerPortalToggle;